import React from "react";
import { pick } from "lodash";
import HelpTrigger from "@/components/HelpTrigger";
import Link from "@/components/Link";
import { Renderer as VisRenderer, Editor as VisEditor, updateVisualizationsSettings } from "@redash/viz/lib";
import { clientConfig } from "@/services/auth";

import countriesDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/countries.geo.json";
import usaDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/usa-albers.geo.json";
import subdivJapanDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/japan.prefectures.geo.json";

import subdivBc100MDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/bc_100M_grid.geo.json";
import subdivBc250MDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/bc_250M_grid.geo.json";
import subdivBcBadmOldDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/bc_Badm_old.geo.json";
import subdivBcHadmOldDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/bc_Hadm_old.geo.json";
import subdivBcBadmDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/bc_Badm.geo.json";
import subdivBcHadmDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/bc_Hadm.geo.json";
import subdivBcHadmGuDataUrl from "@redash/viz/lib/visualizations/choropleth/maps/bc_Hadm_gu.geo.json";

function wrapComponentWithSettings(WrappedComponent) {
  return function VisualizationComponent(props) {
    updateVisualizationsSettings({
      HelpTriggerComponent: HelpTrigger,
      LinkComponent: Link,
      choroplethAvailableMaps: {
        countries: {
          name: "Countries",
          url: countriesDataUrl,
          fieldNames: {
            name: "Short name",
            name_long: "Full name",
            abbrev: "Abbreviated name",
            iso_a2: "ISO code (2 letters)",
            iso_a3: "ISO code (3 letters)",
            iso_n3: "ISO code (3 digits)",
          },
        },
        usa: {
          name: "USA",
          url: usaDataUrl,
          fieldNames: {
            name: "Name",
            ns_code: "National Standard ANSI Code (8-character)",
            geoid: "Geographic ID",
            usps_abbrev: "USPS Abbreviation",
            fips_code: "FIPS Code (2-character)",
          },
        },
        subdiv_japan: {
          name: "Japan/Prefectures",
          url: subdivJapanDataUrl,
          fieldNames: {
            name: "Name",
            name_alt: "Name (alternative)",
            name_local: "Name (local)",
            iso_3166_2: "ISO-3166-2",
            postal: "Postal Code",
            type: "Type",
            type_en: "Type (EN)",
            region: "Region",
            region_code: "Region Code",
          },
        },
        subdiv_Bc100M: {
          name: "Korea/Bc100M",
          url: subdivBc100MDataUrl,
          fieldNames: {
            gid: "GID",
          },
        },
        subdiv_Bc250M: {
          name: "Korea/Bc250M",
          url: subdivBc250MDataUrl,
          fieldNames: {
            gid: "GID",
          },
        },
        subdiv_Bc_Badm_old: {
          name: "Korea/BcBadmOld",
          url: subdivBcBadmOldDataUrl,
          fieldNames: {
            UFID: "UFID",
            BJCD: "BJCD",
            NAME: "NAME",
            DIVI: "DIVI",
            SCLS: "SCLS",
            FMTA: "FMTA",
          },
        },
        subdiv_Bc_Hadm_old: {
          name: "Korea/BcHadmOld",
          url: subdivBcHadmOldDataUrl,
          fieldNames: {
            SIDO_NM: "SIDO_NM",
            SIGUNGU_NM: "SIGUNGU_NM",
            ADM_DR_NM: "ADM_DR_NM",
            pred: "PRED",
          },
        },
        subdiv_Bc_Badm: {
          name: "Korea/BcBadm",
          url: subdivBcBadmDataUrl,
          fieldNames: {
            EMD_CD: "EMD_CD",
            EMD_ENG_NM: "EMD_ENG_NM",
            EMD_KOR_NM: "EMD_KOR_NM",
          },
        },
        subdiv_Bc_Hadm: {
          name: "Korea/BcHadm",
          url: subdivBcHadmDataUrl,
          fieldNames: {
            EMD_CD: "EMD_CD",
            EMD_ENG_NM: "EMD_ENG_NM",
            EMD_KOR_NM: "EMD_KOR_NM",
          },
        },
        subdiv_Bc_Hadm_Gu: {
          name: "Korea/BcHadmGu",
          url: subdivBcHadmGuDataUrl,
          fieldNames: {
            SIG_CD: "SIG_CD",
            SIG_ENG_NM: "SIG_ENG_NM",
            SIG_KOR_NM: "SIG_KOR_NM",
          },
        },
      },
      ...pick(clientConfig, [
        "dateFormat",
        "dateTimeFormat",
        "integerFormat",
        "floatFormat",
        "booleanValues",
        "tableCellMaxJSONSize",
        "allowCustomJSVisualizations",
        "hidePlotlyModeBar",
      ]),
    });

    return <WrappedComponent {...props} />;
  };
}

export const Renderer = wrapComponentWithSettings(VisRenderer);
export const Editor = wrapComponentWithSettings(VisEditor);
